import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  Inbox,
  Package,
  Settings,
  LogOut,
  Menu,
} from 'lucide-react';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { cn } from '../../lib/utils';
import { ThemeToggle } from '../ui/ThemeToggle';

// Hook to detect if we're on desktop
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );

  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  return isDesktop;
}

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
  { icon: Users, label: 'Clients', path: '/clients' },
  { icon: FolderKanban, label: 'Projects', path: '/projects' },
  { icon: Inbox, label: 'Requests', path: '/requests' },
  { icon: Package, label: 'Bundles', path: '/bundles' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

export function Sidebar() {
  const location = useLocation();
  const { sidebarOpen, sidebarCollapsed, toggleSidebar, toggleSidebarCollapse } =
    useUIStore();
  const { logout, user } = useAuthStore();
  const isDesktop = useIsDesktop();

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={toggleSidebar}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Mobile menu button */}
      <button
        onClick={toggleSidebar}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 rounded-xl bg-bg-secondary border border-border"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{
          x: isDesktop ? 0 : (sidebarOpen ? 0 : '-100%'),
          width: sidebarCollapsed ? 80 : 256,
        }}
        className={cn(
          'fixed top-0 left-0 h-full z-50 lg:z-30',
          'bg-bg-secondary/80 backdrop-blur-xl border-r border-border',
          'flex flex-col'
        )}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {/* Logo - clickable to toggle sidebar */}
        <button
          onClick={toggleSidebarCollapse}
          className="h-16 flex items-center justify-center px-4 border-b border-border w-full hover:bg-bg-tertiary/50 transition-colors"
        >
          <img
            src="/OMLYodaLogo.png"
            alt="OML Yoda"
            className={cn('transition-all', sidebarCollapsed ? 'h-6' : 'h-8')}
          />
        </button>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => {
            const isActive =
              item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path);

            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => sidebarOpen && toggleSidebar()}
                className="block"
              >
                <motion.div
                  whileHover={{ scale: 1.02, x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors relative',
                    isActive
                      ? 'text-accent'
                      : 'text-text-secondary hover:text-text hover:bg-bg-tertiary'
                  )}
                >
                  {/* Active indicator */}
                  {isActive && (
                    <motion.div
                      layoutId="activeNav"
                      className="absolute inset-0 bg-accent/10 rounded-xl"
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    />
                  )}

                  <item.icon className="w-5 h-5 relative z-10" />

                  <AnimatePresence mode="wait">
                    {!sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        className="font-medium relative z-10"
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>

                  {/* Active glow */}
                  {isActive && (
                    <motion.div
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-accent rounded-r-full"
                      layoutId="activeGlow"
                    />
                  )}
                </motion.div>
              </NavLink>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-3 border-t border-border space-y-3">
          {/* Theme toggle */}
          <div className={cn('flex', sidebarCollapsed ? 'justify-center' : 'justify-start px-2')}>
            <AnimatePresence mode="wait">
              {!sidebarCollapsed ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center justify-between w-full"
                >
                  <span className="text-xs text-text-muted">Theme</span>
                  <ThemeToggle />
                </motion.div>
              ) : (
                <ThemeToggle />
              )}
            </AnimatePresence>
          </div>

          <div
            className={cn(
              'flex items-center gap-3 p-2 rounded-xl',
              sidebarCollapsed ? 'justify-center' : ''
            )}
          >
            <motion.div
              whileHover={{ scale: 1.1 }}
              className="w-9 h-9 rounded-full bg-gradient-to-br from-accent to-accent-light flex items-center justify-center flex-shrink-0 shadow-lg shadow-accent/20"
            >
              <span className="text-white font-semibold text-sm">
                {user?.name?.charAt(0) || 'A'}
              </span>
            </motion.div>

            <AnimatePresence mode="wait">
              {!sidebarCollapsed && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="flex-1 min-w-0"
                >
                  <p className="font-medium text-sm truncate">{user?.name}</p>
                  <p className="text-xs text-text-muted truncate">{user?.email}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Logout button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => logout()}
            className={cn(
              'flex items-center gap-3 w-full px-3 py-2.5 rounded-xl',
              'text-text-secondary hover:text-red-500 hover:bg-red-500/10 transition-colors',
              sidebarCollapsed ? 'justify-center' : ''
            )}
          >
            <LogOut className="w-5 h-5" />
            <AnimatePresence mode="wait">
              {!sidebarCollapsed && (
                <motion.span
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="font-medium"
                >
                  Logout
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </motion.aside>
    </>
  );
}
